export interface WaveWeek {
  week: number
  event: string
  date: string
}

export interface WavePlanningFixture {
  id: string
  name: string
  code: string
  description: string
  season: string
  stores: number
  previousStores: number | null
  skus: number
  previousSkus: number | null
  pogSet: number
  pogMax: number | null
  purchaseUnits: number
  setDate: string
  setWeek: number
  seasonEndWeek: number
  note: string | null
  demand: number[]
  historicalWaves: number[]
  plannedWaves: number[]
}

export interface CalculatedWaveWeek extends WaveWeek {
  historicalWave: number
  plannedWave: number
  cumulativeWave: number
  cumulativeDemandPercent: number
  receipts: number
  averageReceipts: number
  inventory: number
  averageInventory: number
  receiptsInStores: number
  salesInStores: number
  variance: number
}

const divide = (value: number, denominator: number) =>
  denominator === 0 ? 0 : value / denominator

/** Inputs are fractions (0.78 = 78%). Keep full precision until presentation. */
export function calculateWavePlanning(
  fixture: WavePlanningFixture,
  weeks: WaveWeek[],
  percentages: number[]
) {
  const totalDemand = fixture.demand.reduce((sum, value) => sum + value, 0)
  let cumulativeWave = 0
  let cumulativeDemand = 0
  let inventory = 0

  const calculated: CalculatedWaveWeek[] = weeks.map((week, index) => {
    const plannedWave = week.event === 'NO WAVE' ? 0 : (percentages[index] ?? 0)
    const demand = fixture.demand[index] ?? 0
    const receipts = plannedWave * fixture.purchaseUnits
    cumulativeWave += plannedWave
    cumulativeDemand += demand
    inventory += receipts - demand
    // Excel row 31: 50% arrives in the wave week, 50% the following week.
    const receiptsInStores = cumulativeWave - plannedWave / 2
    const salesInStores = divide(cumulativeDemand, totalDemand)

    return {
      ...week,
      historicalWave: fixture.historicalWaves[index] ?? 0,
      plannedWave,
      cumulativeWave,
      cumulativeDemandPercent: divide(cumulativeDemand, fixture.purchaseUnits),
      receipts,
      averageReceipts: divide(receipts, fixture.stores),
      inventory,
      averageInventory: divide(inventory, fixture.stores),
      receiptsInStores,
      salesInStores,
      // Deliberately correct the shifted references in Excel AK33:AP33.
      variance: receiptsInStores - salesInStores,
    }
  })

  const inSeasonDemandPercent =
    calculated.find(week => week.week === fixture.seasonEndWeek)
      ?.cumulativeDemandPercent ?? 0
  const minimumFirstWave = fixture.purchaseUnits
    ? (fixture.pogSet * fixture.stores) / fixture.purchaseUnits + 0.1
    : 0

  return {
    weeks: calculated,
    total: cumulativeWave,
    minimumFirstWave,
    firstWave: calculated.find(week => week.plannedWave > 0)?.plannedWave ?? 0,
    inSeasonDemandPercent,
    averageStoreReceipts: divide(fixture.purchaseUnits, fixture.stores),
    averageStoreSales: divide(
      fixture.purchaseUnits * inSeasonDemandPercent,
      fixture.stores
    ),
  }
}

export function validateWavePercent(value: string): string | null {
  const input = value.trim()
  if (!input) return null
  if (!/^\d+(?:\.\d{0,2})?$/.test(input) || Number(input) > 100) {
    return 'Enter 0–100 with up to 2 decimal places.'
  }
  return null
}

export function shiftWaveDate(date: string, days: number): string {
  const value = new Date(`${date}T00:00:00Z`)
  value.setUTCDate(value.getUTCDate() + days)
  return value.toISOString().slice(0, 10)
}

export const formatPercent = (value: number, decimals = 2) =>
  new Intl.NumberFormat('en-US', {
    style: 'percent',
    maximumFractionDigits: decimals,
  }).format(Math.abs(value * 100) < 0.5 * 10 ** -decimals ? 0 : value)

export const formatUnits = (value: number) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(
    Math.abs(value) < 0.5 ? 0 : value
  )

export const formatWaveDate = (date: string) =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`))

export const weekLabel = (week: number) => `WK${String(week).padStart(2, '0')}`
